/* AlpineJS is our main JS utility framework, doc here : https://alpinejs.dev/start-here */
import Alpine from 'alpinejs';
import { initAlpineJs } from './common/Alpine.js';

/**
 * Alpine components must be registered before Alpine.start().
 * The listener is added at top-level so it's registered before the
 * DOMContentLoaded callback below runs Alpine.start().
 */
document.addEventListener('alpine:init', () => {
  initAlpineJs();
});

async function loadModule(loader, name) {
  try {
    return await loader();
  } catch (err) {
    console.warn(`[hwc] failed to load module: ${name}`, err);
    return undefined;
  }
}

function updateVh() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
}

document.addEventListener('DOMContentLoaded', async () => {

  updateVh();

  /* Starts Alpine */
  window.Alpine = Alpine;
  Alpine.start();

  /* Map (Leaflet is loaded externally as window.L) */
  const mapEl = document.querySelector('[data-hwc-map]');
  if (mapEl) {
    const mod = await loadModule(() => import('./blocks/Map.js'), 'Map');
    if (mod) new mod.default(mapEl);
  }

  /* CountUp triggers */
  const countUpEls = document.querySelectorAll('.js-countup');
  if (countUpEls.length) {
    const mod = await loadModule(() => import('./common/CountUp.js'), 'CountUp');
    if (mod) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.intersectionRatio > 0) {
            new mod.CountUp(entry.target, parseInt(entry.target.textContent), { separator: '', duration: 3 }).start();
            observer.unobserve(entry.target);
          }
        });
      }, { root: null, threshold: 0, rootMargin: '-50px' });
      countUpEls.forEach((el) => observer.observe(el));
    }
  }

  /* Sliders (Swiper) */
  const sliders = document.querySelectorAll('[data-hwc-slider]');
  if (sliders.length) {
    const mod = await loadModule(() => import('./common/Slider.js'), 'Slider');
    if (mod) sliders.forEach((slider) => new mod.default(slider));
  }

  /* Anchors */
  if (document.querySelector('.hwc-Anchors')) {
    const mod = await loadModule(() => import('./common/Anchors.js'), 'Anchors');
    if (mod) new mod.default();
  }

  /* SliderRotate (Swiper creative effect) */
  const slidersRotate = document.querySelectorAll('[data-hwc-slider-rotate]');
  if (slidersRotate.length) {
    const mod = await loadModule(() => import('./common/SliderRotate.js'), 'SliderRotate');
    if (mod) slidersRotate.forEach((slider) => new mod.default(slider));
  }

  /* Lightbox (BiggerPicture) */
  const lightboxes = document.querySelectorAll('[data-hwc-lightbox]');
  if (lightboxes.length) {
    const mod = await loadModule(() => import('bigger-picture'), 'BiggerPicture');
    if (mod && typeof mod.default === 'function') {
      const bp = mod.default({ target: document.body });
      lightboxes.forEach((lightbox) => {
        const links = [...lightbox.querySelectorAll('[data-lightbox-link]')];
        for (const link of links) {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            bp.open({
              items: links,
              el: e.currentTarget,
              intro: 'fadeup',
              scale: 0.9,
            });
          });
        }
      });
    }
  }

  /* Replace <a href="#"> by <div> in inline menu (Alpine.initTree required after Alpine.start) */
  document.querySelectorAll('.hwc-MenuInline-link[href="#"]').forEach(link => {
    const div = document.createElement('div');
    div.innerHTML = link.innerHTML;
    div.classList.add('hwc-MenuInline-link');
    div.setAttribute('x-bind', 'toggler');
    link.parentNode.replaceChild(div, link);
    Alpine.initTree(div);
  });
  document.querySelectorAll('.hwc-Panel-menu-link[href="#"]').forEach(link => {
    const button = document.createElement('button');
    button.innerHTML = link.innerHTML;
    button.classList.add('hwc-Panel-menu-link');
    button.classList.add('hwc-Intro');
    link.parentNode.replaceChild(button, link);
  });

  /* Submenu Panel */
  document.querySelectorAll('.hwc-Panel-menu-link:has(.hwc-Panel-menu-arrow)').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();

      const child = el.nextElementSibling;
      if (child && child.matches('.hwc-Panel-submenu')) {
        child.classList.add('active');

        const close = child.querySelector('.hwc-Panel-submenu-return button');
        if (close) {
          close.addEventListener('click', (ev) => {
            ev.preventDefault();
            child.classList.remove('active');
          });
        }
      }
    });
  });

  /* Salesforce */
  document.addEventListener('wpcf7mailsent', function (event) {
    if (hwc_vars.salesforce_mode !== 'test') return;

    const allowedFormIds = (hwc_vars.salesforce_cf7_ids || []).map((id) => String(id));
    const currentFormId = String(event.detail.contactFormId);
    if (!allowedFormIds.includes(currentFormId)) return;

    const inputs = event.detail.inputs || [];

    const getValue = (name) => {
      const found = inputs.find((item) => item.name === name);
      return found ? found.value : '';
    };

    const url = new URL(hwc_vars.salesforce_endpoint);

    const params = {
      first_name: getValue('hwc-firstname'),
      last_name: getValue('hwc-lastname'),
      email: getValue('hwc-email'),
      mobile: getValue('hwc-phone'),
      lead_source: 'Rénolia',
      Campaign_ID: hwc_vars.salesforce_campaign_id,
      '00N7Q00000J4p86': 'A qualifier',
      '00N7Q00000J4uno': getValue('hwc-postcode'),
      '00N7Q00000J4p8p': getValue('hwc-message'),
      '00N7Q00000J4ulL': getValue('hwc-city'),
      '00N7Q00000J4pGL': 'A définir',
      Company: getValue('hwc-company'),
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== '') {
        url.searchParams.set(key, value);
      }
    });

    //console.log(url.toString());

  }, false);

  // ----- 

  const configurators = document.querySelectorAll('[data-hwc-configurator]');
  if (configurators.length) {
    const mod = await loadModule(() => import('./common/Configurator.js'), 'Configurator');
    if (mod && typeof mod.default === 'function') {
      configurators.forEach((configurator) => new mod.default(configurator));
    }
  }

  // ----- 

  /* Adds class for reveal */
  const elements = document.querySelectorAll('.js-reveal');

  if (!('IntersectionObserver' in window)) {
    elements.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target); // animation une seule fois
      }
    });
  }, {
    threshold: 0.2
  });

  elements.forEach(el => observer.observe(el));

  document.body.classList.add('is-domready');

});

window.addEventListener('load', () => {
  document.body.classList.add('is-loaded');
});

window.addEventListener('resize', updateVh);
